import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Icon, Text, ActionMenu, Skeleton, Button } from '../../../../components';
import { useSheetDismiss } from '@/nova/components/Sheet';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { MOBILE } from '@/nova/theme/tokens';
import type { CreativeItem } from '../../creative-api';
import { useSortableGrid } from '../../hooks/useSortableGrid';
import type { OrderedSourceStatus } from '../../hooks/useOrderedSource';
import {
  Wrap,
  Head,
  LeadWrap,
  Lead,
  LeadHint,
  Actions,
  PreviewBtn,
  PublishBtn,
  Buttons,
  DirtyNote,
  KebabWrap,
  Grid,
  Slot,
  DragLayer,
  DragGhost,
  SlotCard,
  CardArt,
  CardImg,
  TitleRow,
  DragHandle,
  CardTitle,
  EditBtn,
  MoveBtns,
  MoveBtn,
  OrderBadge,
  SlotBtns,
  SlotBtn,
  EmptyState,
  EmptyTitle,
  EmptyText,
  AddTile,
  EmptyAddTile,
  AddTilePlus,
  AddTileLabel,
} from './CarouselCuration.styles';
import { PoolPickerSheet } from '../PoolPickerSheet';

export type CarouselCurationProps = {
  /** The carousel's display name (e.g. "Homepage"), for copy. */
  label: string;
  /** Every banner in the pool — the library the owner picks from. */
  poolItems: CreativeItem[];
  /** The current WORKING selection (draft) — edited live by add / remove / reorder. */
  selectedIds: string[];
  /** What is currently PUBLISHED to the site — the draft is "dirty" while it differs from this. */
  baselineIds: string[];
  /** Where the read of the carousel's order stands. Until it has answered (`idle` / `loading`) the grid is a
   *  skeleton, and a `failed` read is an error with a retry: the empty copy is only for a read that answered with
   *  no banners. `unavailable` (no tenant-source route in this environment) shows the curation as before.
   *  Defaults to `ready`. */
  loadStatus?: OrderedSourceStatus;
  /** Read the carousel's order again (the error state's retry). */
  onRetry?: () => void;
  /** Update the working draft (not persisted until published). */
  onChange: (ids: string[]) => void;
  /** Commit the working draft to the site (persist the ordered selection). */
  onPublish: () => void | Promise<void>;
  /** True when the last publish can be undone (a differing previously published order exists). */
  canRevert?: boolean;
  /** Undo the last publish — restore the previously published order. Rendered only when canRevert. */
  onRevert?: () => void | Promise<void>;
  /** Open the pool editor for one banner — lets a curated card be retouched in place. */
  onEditBanner: (item: CreativeItem) => void;
  /** Preview this carousel IN-APP on the embedded site (its page, in the current draft order). Optional
   *  bannerId highlights one banner. When absent, no preview affordance is shown. */
  onPreview?: (bannerId?: string) => void;
};

/** Two id lists are equal when same length + same order. */
const sameOrder = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * A curation carousel (Homepage / Casino / Sports): the owner picks banners from the pool and orders
 * them, working on a DRAFT they then commit with "Publish to site". Selection-first — the ordered,
 * drag-to-reorder grid IS the view, and "Add banners" opens the pool as a picker. Banners are authored +
 * published in the pool ("all Promotions"); this only records which appear here and in what order.
 */
export const CarouselCuration = ({
  label,
  poolItems,
  selectedIds,
  baselineIds,
  loadStatus = 'ready',
  onRetry,
  onChange,
  onPublish,
  canRevert,
  onRevert,
  onEditBanner,
  onPreview,
}: CarouselCurationProps): React.ReactElement => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(MOBILE);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const { closeHandle: pickerCloseHandle } = useSheetDismiss(() => setPickerOpen(false));
  const [publishing, setPublishing] = React.useState(false);
  const [reverting, setReverting] = React.useState(false);
  // The draft is "dirty" (has unsaved curation) while it differs from what's published to the site.
  const dirty = !sameOrder(selectedIds, baselineIds);

  // Pool banners keyed by id, so the ordered selection can resolve each to its card (selections that no
  // longer exist in the pool — e.g. an archived banner — are skipped).
  const byId = React.useMemo(() => {
    const m = new Map<string, CreativeItem>();
    for (const it of poolItems) m.set(it.banner_id, it);
    return m;
  }, [poolItems]);

  const selected = React.useMemo(
    () => selectedIds.map((id) => byId.get(id)).filter((it): it is CreativeItem => Boolean(it)),
    [selectedIds, byId],
  );
  const cleanIds = React.useMemo(() => selected.map((it) => it.banner_id), [selected]);

  const cardLabel = React.useCallback(
    (item: CreativeItem): string => item.headline || item.internal_name || t('Banner'),
    [t],
  );

  const removeAt = React.useCallback(
    (id: string): void => onChange(cleanIds.filter((x) => x !== id)),
    [cleanIds, onChange],
  );
  // Realtime drag reorder (desktop) and neighbour swaps (mobile, where dragging is disabled so the list
  // stays scrollable). See hooks/useSortableGrid.
  const sortable = useSortableGrid(cleanIds, onChange);
  const toggle = React.useCallback(
    (id: string): void => onChange(cleanIds.includes(id) ? cleanIds.filter((x) => x !== id) : [...cleanIds, id]),
    [cleanIds, onChange],
  );

  // The card currently being dragged (desktop) — resolved so the custom drag element can mirror it.
  const draggedItem = sortable.draggedId ? byId.get(sortable.draggedId) ?? null : null;

  return (
    <Wrap>
      <Head>
        <LeadWrap>
          <Lead
            text="Pick banners from your pool and order them for the {{label}} carousel."
            placeholders={{ label }}
          />
          <LeadHint>
            <Icon name={isMobile ? 'chevron-expand' : 'grab'} size={18} />
            {isMobile ? (
              <Text tag="span" text="Use the up and down arrows to reorder, then Publish to site to commit." />
            ) : (
              <Text tag="span" text="Drag to reorder, then Publish to site to commit." />
            )}
          </LeadHint>
        </LeadWrap>
        <Actions>
          {/* Revert lives in the kebab (which closes on select), so surface its progress here where it stays visible. */}
          {reverting ? (
            <DirtyNote>{t('Reverting…')}</DirtyNote>
          ) : dirty && !publishing ? (
            <DirtyNote>{t('Draft · not published')}</DirtyNote>
          ) : null}
          <Buttons>
            {onPreview ? (
              // The same Preview on site as a game list's: the Games tab's Add list look, the preview glyph.
              <PreviewBtn variant="outline" icon="preview" label="Preview on site" onClick={() => onPreview()} />
            ) : null}
            <PublishBtn
              type="button"
              disabled={!dirty || publishing}
              onClick={async () => {
                setPublishing(true);
                try {
                  await onPublish();
                } finally {
                  setPublishing(false);
                }
              }}
            >
              <Icon name="globe" size={14} />
              {publishing ? <Text tag="span" text="Publishing…" /> : <Text tag="span" text="Publish to site" />}
            </PublishBtn>
          </Buttons>
          {canRevert && onRevert ? (
            <KebabWrap>
              <ActionMenu
                align="end"
                label="More actions"
                disabled={publishing || reverting}
                items={[
                  {
                    label: reverting ? 'Reverting…' : 'Revert to initial state',
                    icon: 'arrow-left',
                    disabled: reverting || publishing,
                    onSelect: () => {
                      setReverting(true);
                      void (async () => {
                        try {
                          await onRevert();
                        } finally {
                          setReverting(false);
                        }
                      })();
                    },
                  },
                ]}
              />
            </KebabWrap>
          ) : null}
        </Actions>
      </Head>

      {loadStatus === 'idle' || loadStatus === 'loading' ? (
        <Grid aria-busy="true">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} block height={196} radius={14} />
          ))}
        </Grid>
      ) : loadStatus === 'failed' ? (
        <EmptyState>
          <EmptyTitle text="We couldn't load this list" />
          <EmptyText text="Please try again." />
          {onRetry ? <Button variant="outline" icon="refresh" label={t('Try again')} onClick={onRetry} /> : null}
        </EmptyState>
      ) : selected.length === 0 ? (
        <EmptyState>
          <EmptyTitle text="No banners in this carousel yet" />
          <EmptyText text="Add banners from your pool to show them here, in the order you choose." />
          <EmptyAddTile type="button" aria-label={t('Add banners')} onClick={() => setPickerOpen(true)}>
            <AddTilePlus aria-hidden="true">
              <Icon name="plus" size={22} />
            </AddTilePlus>
            <AddTileLabel text="Add banner" />
          </EmptyAddTile>
        </EmptyState>
      ) : (
        <Grid onDragOver={isMobile ? undefined : sortable.onGridDragOver} onDrop={(e) => e.preventDefault()}>
          {/* Opens the pool picker; placed FIRST so "add" is the leading affordance (the header button is gone). */}
          <AddTile type="button" aria-label={t('Add banners')} onClick={() => setPickerOpen(true)}>
            <AddTilePlus aria-hidden="true">
              <Icon name="plus" size={22} />
            </AddTilePlus>
            <AddTileLabel text="Add banner" />
          </AddTile>
          {selected.map((item, i) => (
            <Slot
              key={item.banner_id}
              ref={sortable.registerSlot(item.banner_id)}
              draggable={!isMobile}
              $dragging={sortable.draggedId === item.banner_id}
              // Record the grab point, hide the native drag ghost and mount our own follow element
              // (rendered solid + shadowed, no snapshot clipping). See DragLayer below.
              {...sortable.slotDragProps(item.banner_id)}
            >
              <OrderBadge>{i + 1}</OrderBadge>
              <SlotBtns>
                <SlotBtn type="button" aria-label={t('Remove from carousel')} onClick={() => removeAt(item.banner_id)}>
                  <Icon name="close" size={13} />
                </SlotBtn>
              </SlotBtns>
              <SlotCard>
                <CardArt>
                  {item.image_url ? <CardImg src={item.image_url} alt="" loading="lazy" draggable={false} /> : null}
                </CardArt>
                <TitleRow>
                  {isMobile ? (
                    <MoveBtns>
                      <MoveBtn
                        type="button"
                        aria-label={t('Move up')}
                        disabled={i === 0}
                        onClick={() => sortable.move(item.banner_id, -1)}
                      >
                        <Icon name="chevron-up" size={18} />
                      </MoveBtn>
                      <MoveBtn
                        type="button"
                        $down
                        aria-label={t('Move down')}
                        disabled={i === selected.length - 1}
                        onClick={() => sortable.move(item.banner_id, 1)}
                      >
                        <Icon name="chevron-up" size={18} />
                      </MoveBtn>
                    </MoveBtns>
                  ) : (
                    <DragHandle aria-hidden="true">
                      <Icon name="drag-handle" size={28} />
                    </DragHandle>
                  )}
                  <CardTitle text={cardLabel(item)} />
                  <EditBtn type="button" aria-label={t('Edit banner')} onClick={() => onEditBanner(item)}>
                    <Icon name="edit" size={15} />
                  </EditBtn>
                </TitleRow>
              </SlotCard>
            </Slot>
          ))}
        </Grid>
      )}

      {/* Custom drag element: our own solid, shadowed card that follows the pointer (the native drag
          ghost is suppressed), portalled to <body> so nothing clips it. Position is driven imperatively
          via transform; this inline one only seeds the first frame + survives reorder re-renders. */}
      {draggedItem && typeof document !== 'undefined'
        ? createPortal(
            <DragLayer ref={sortable.dragLayerRef} aria-hidden="true" style={{ transform: sortable.ghostTransform }}>
              <DragGhost style={{ width: `${sortable.ghostWidth}px` }}>
                <CardArt>
                  {draggedItem.image_url ? (
                    <CardImg src={draggedItem.image_url} alt="" draggable={false} />
                  ) : null}
                </CardArt>
                <TitleRow>
                  <DragHandle aria-hidden="true">
                    <Icon name="drag-handle" size={28} />
                  </DragHandle>
                  <CardTitle text={cardLabel(draggedItem)} />
                </TitleRow>
              </DragGhost>
            </DragLayer>,
            document.body,
          )
        : null}

      {pickerOpen ? (
        <PoolPickerSheet
          title="Add banners to this carousel"
          poolItems={poolItems}
          selectedIds={cleanIds}
          labelOf={cardLabel}
          onToggle={toggle}
          onClose={() => setPickerOpen(false)}
          closeHandle={pickerCloseHandle}
          emptyText="Your pool is empty. Create banners in All Promotions & Creatives first."
        />
      ) : null}
    </Wrap>
  );
};

export default CarouselCuration;
